import { PatchDataWrapper } from '@diamond/shared/data-access';
import { BackofficeActivitiesOrderItem } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { Row } from '@tanstack/react-table';
import { useCallback } from 'react';

import { ORDER_CHANGELOG_QUERY_KEY } from '../api';
import { ActivityDetailProps } from '../types/activity-column-types';

const API_URL = 'backoffice/order';

export function useActivityDetail({
  selectedData,
  setSelectedData,
  refetch,
  activitiesId,
  queryKey = [],
  token,
  toast,
}: ActivityDetailProps) {
  const queryClient = useQueryClient();

  const { patchData } = PatchDataWrapper<unknown>(
    API_URL,
    token || '',
    'admin'
  );

  const handleSelectAll = useCallback(
    (values: Row<BackofficeActivitiesOrderItem>[], isChecked: boolean) => {
      if (!isChecked) {
        setSelectedData([...values]);
      } else {
        setSelectedData([]);
      }
    },
    [setSelectedData]
  );

  const handleSelectOneRow = useCallback(
    (value: Row<BackofficeActivitiesOrderItem>, isChecked: boolean) => {
      if (isChecked) {
        setSelectedData(selectedData.filter((row) => row.id !== value.id));
      } else {
        setSelectedData([...selectedData, value]);
      }
    },
    [selectedData, setSelectedData]
  );

  const handlePatchData = useCallback(
    async (
      data: Record<string, unknown>,
      pathSuffix = '',
      itemPath?: string
    ) => {
      const fullPath = itemPath
        ? `${activitiesId}/item/${itemPath}`
        : activitiesId;

      const result = await patchData(data, pathSuffix, fullPath);

      refetch();
      await queryClient.invalidateQueries({
        queryKey: [ORDER_CHANGELOG_QUERY_KEY, activitiesId],
      });

      return result;
    },
    [activitiesId, patchData, queryClient, refetch]
  );

  const handleShowToast = useCallback(
    (type: 'success' | 'error', message: string) => {
      showToast(toast, type, message);
    },
    [toast]
  );

  return {
    selectedData,
    setSelectedData,
    refetch,
    activitiesId,
    queryKey,
    handleSelectAll,
    handleSelectOneRow,
    handlePatchData,
    handleShowToast,
  };
}
